import { useCallback, useEffect, useState } from "react";
import {
  ALERT_FREQUENCY_LABELS,
  ALERT_GRACE_SECONDS,
  centsFromInput,
  DIGEST_HOUR,
  formatMoney,
  priceFromCents,
  priceWithMarkup,
  reserveOwner,
  reserveShortfall,
  reserveTagLine,
  todayLocal,
  type AlertFrequency,
  type AlertPrefs,
  type Client,
  type Reserve,
  type ReserveDetail,
  type ReserveHold,
  type ReserveInput,
  type ReserveQuietReason,
  type ReserveTag,
  formatCount,
} from "@iltocat/shared";
import { ApiError, api, plural } from "../lib/api.js";
import { calendarDay } from "../lib/stockFormat.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { ClientTag, clientColors } from "../components/ClientTag.js";
import { Dialog } from "../components/Dialog.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon, type IconName } from "../components/Icon.js";
import { Menu, MenuItem, Segmented } from "@vcnet/ui";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { ReserveTagChip, ReserveTagPicker } from "../components/ReserveTags.js";
import { useToast } from "../components/Toast.js";

/**
 * Back stock: reserves, what they hold, and who hears when they run short.
 *
 * A reserve is a rule -- these tags, for this client, at least this many --
 * and a card per rule is the whole screen: how full it is against its
 * minimum and its fill-up number, whether the client pays in advance, and the
 * buttons that fill it, quiet it ("ordered, due the 3rd") or change it.
 *
 * Cards are grouped under the client they are for, the general pool last, and
 * a card is headed by what it keeps back -- its tags -- because a client with
 * five reserves used to be five cards headed with the same name. "Held units"
 * opens what it holds and the matching stock it could take in a dialog: a
 * table, which inline stretched the card across the whole row.
 *
 * Nothing here mails anybody on the spot. The alert settings at the bottom
 * are each person's own, and a digest only mentions a reserve that has stayed
 * short past the grace period -- so shuffling stock between clients does not
 * set anybody's phone off.
 */
export function Reserves({ vocabulary, onOpen }: { vocabulary: Vocabulary; onOpen: (id: string) => void }) {
  const [reserves, setReserves] = useState<Reserve[] | null>(null);
  const [alerts, setAlerts] = useState<AlertPrefs | null>(null);
  const [reserveTags, setReserveTags] = useState<ReserveTag[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [markup, setMarkup] = useState(0);
  const [editing, setEditing] = useState<Reserve | "new" | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, clientList, settings] = await Promise.all([
        api.reserves.list(),
        api.clients.list(),
        api.stock.settings(),
      ]);
      setReserves(list.reserves);
      setAlerts(list.alerts);
      setReserveTags(list.reserveTags);
      setClients(clientList.clients);
      setMarkup(settings.settings.markupPercent);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load the reserves.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The digest's "change or stop them" link lands on the settings, not the top.
  useEffect(() => {
    if (alerts && window.location.hash === "#alerts") {
      document.getElementById("alerts")?.scrollIntoView({ block: "start" });
    }
  }, [alerts]);

  // The item screen's tag box offers reserve tags too, so it hears about a change.
  const changeTags = (next: ReserveTag[]) => {
    setReserveTags(next);
    void vocabulary.reloadReserveTags();
  };

  const colors = clientColors(clients);
  const replace = (next: Reserve) =>
    setReserves((now) => (now ?? []).map((one) => (one.id === next.id ? next : one)));

  const short = (reserves ?? []).filter((one) => one.status === "short");
  const quiet = (reserves ?? []).filter((one) => one.status === "quiet");
  const held = (reserves ?? []).reduce((sum, one) => sum + (one.clientId ? one.count : 0), 0);
  const owed = (reserves ?? []).reduce((sum, one) => sum + one.credit, 0);
  const stats: { icon: IconName; label: string; value: number; tone?: string }[] = [
    { icon: "alert", label: "Short", value: short.length, ...(short.length ? { tone: "stat-danger" } : {}) },
    { icon: "clock", label: "Ordered or snoozed", value: quiet.length },
    { icon: "bookmark", label: "Units held for clients", value: held },
    { icon: "lock", label: "Paid for, not yet held", value: owed },
  ];

  return (
    <>
      <PageHeader
        title="Reserves"
        actions={
          <button
            type="button"
            className="button"
            onClick={() => setEditing("new")}
            disabled={editing !== null}
          >
            <Icon name="plus" size={18} />
            New reserve
          </button>
        }
      />
      {error && <Notice tone="error">{error}</Notice>}

      <ul className="stats" aria-busy={reserves === null}>
        {reserves === null
          ? Array.from({ length: 4 }, (_, index) => <li key={index} className="stat skeleton" />)
          : stats.map((stat) => (
              <li key={stat.label} className={stat.tone ? `stat ${stat.tone}` : "stat"}>
                <span className="stat-icon">
                  <Icon name={stat.icon} size={18} />
                </span>
                <span className="stat-value">{formatCount(stat.value)}</span>
                <span className="stat-label">{stat.label}</span>
              </li>
            ))}
      </ul>

      {editing !== null && (
        <ReserveForm
          // A new key per reserve: Edit on another card starts the form afresh
          // with that one's details, instead of keeping the first one's.
          key={editing === "new" ? "new" : editing.id}
          reserve={editing === "new" ? null : editing}
          clients={clients}
          reserveTags={reserveTags}
          onReserveTags={changeTags}
          onClients={setClients}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
          onDeleted={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {reserves === null ? (
        <div className="skeleton-panel" />
      ) : reserves.length === 0 ? (
        editing === null && (
          <Panel>
            <EmptyState
              icon="bookmark"
              title="No reserves yet."
              action={
                <button type="button" className="button" onClick={() => setEditing("new")}>
                  New reserve
                </button>
              }
            >
              A reserve keeps back stock for a client by its tags: say, at least four laptops tagged “17-inch”
              and “256 GB” for Branch A. Stock is pulled into it and marked as held, and you hear about it
              when it runs low.
            </EmptyState>
          </Panel>
        )
      ) : (
        reserveGroups(reserves).map((group) => (
          <section key={group.key} className="reserve-group" aria-labelledby={`reserve-group-${group.key}`}>
            <h2 id={`reserve-group-${group.key}`} className="reserve-group-title">
              {group.key === "pool" ? (
                <>
                  <Icon name="box" size={18} />
                  General pool
                </>
              ) : (
                <ClientTag name={group.name} color={colors.get(group.key) ?? null} />
              )}
              <span className="reserve-group-count">{plural(group.reserves.length, "reserve")}</span>
            </h2>
            <div className="reserve-list">
              {group.reserves.map((reserve) => (
                <ReserveCard
                  key={reserve.id}
                  reserve={reserve}
                  markup={markup}
                  open={open === reserve.id}
                  onOpen={() => setOpen(reserve.id)}
                  onClose={() => setOpen(null)}
                  onEdit={() => setEditing(reserve)}
                  onChanged={replace}
                  onOpenItem={onOpen}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {reserves !== null && (
        <ReserveTagsPanel
          tags={reserveTags}
          onChange={changeTags}
          onRenamed={() => {
            // The rules on the cards and every item's tags were respelled too.
            void load();
            void vocabulary.reloadTags();
          }}
        />
      )}

      {alerts && <AlertsPanel prefs={alerts} onSaved={setAlerts} />}
    </>
  );
}

/* ------------------------------------------------------------- one card -- */

/** The cards by who they are for: clients by name, then the general pool. */
function reserveGroups(reserves: Reserve[]): { key: string; name: string; reserves: Reserve[] }[] {
  const groups = new Map<string, { key: string; name: string; reserves: Reserve[] }>();
  for (const reserve of reserves) {
    const key = reserve.clientId ?? "pool";
    const group = groups.get(key) ?? { key, name: reserveOwner(reserve), reserves: [] };
    group.reserves.push(reserve);
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) =>
    a.key === "pool" ? 1 : b.key === "pool" ? -1 : a.name.localeCompare(b.name),
  );
}

function ReserveCard({
  reserve,
  markup,
  open,
  onOpen,
  onClose,
  onEdit,
  onChanged,
  onOpenItem,
}: {
  reserve: Reserve;
  markup: number;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onEdit: () => void;
  onChanged: (reserve: Reserve) => void;
  onOpenItem: (id: string) => void;
}) {
  const [quieting, setQuieting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<ReserveDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const toast = useToast();
  const pool = reserve.clientId === null;
  const canFill = !pool && reserve.count < reserve.target && reserve.free > 0;

  const loadDetail = useCallback(async () => {
    try {
      setDetail(await api.reserves.detail(reserve.id));
    } catch (problem) {
      setDetailError(problem instanceof ApiError ? problem.message : "Could not load what it holds.");
    }
  }, [reserve.id]);

  useEffect(() => {
    if (open) void loadDetail();
  }, [open, loadDetail, reserve.count]);

  const settle = (next: ReserveDetail) => {
    setDetail(next);
    onChanged(next.reserve);
  };

  async function fill() {
    setBusy(true);
    setError(null);
    try {
      const next = await api.reserves.fill(reserve.id, todayLocal());
      const added = next.reserve.count - reserve.count;
      toast(
        added > 0
          ? `Held ${plural(added, "more unit")} for ${reserveOwner(reserve)}.`
          : "Nothing free to hold.",
      );
      settle(next);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not fill.");
    } finally {
      setBusy(false);
    }
  }

  async function unquiet() {
    setBusy(true);
    try {
      const { reserve: next } = await api.reserves.quiet(reserve.id, {
        until: null,
        reason: "ordered",
        note: "",
      });
      onChanged(next);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not clear.");
    } finally {
      setBusy(false);
    }
  }

  const fullness = reserve.target > 0 ? Math.min(reserve.count / reserve.target, 1) : 1;
  const floor = reserve.target > 0 ? Math.min(reserve.minimum / reserve.target, 1) : 0;

  return (
    <article className={`reserve-card reserve-${reserve.status}`}>
      <header className="reserve-head">
        <div className="reserve-who">
          <h3 className="reserve-title">
            <Icon name="tag" size={16} />
            {reserveTagLine(reserve.tags)}
          </h3>
          <p className="reserve-for">
            {pool ? "Counted on the shelf, for anybody" : `Kept back for ${reserve.clientName}`}
            {reserve.prepaid && " · paid in advance"}
          </p>
        </div>
        <StatusPill reserve={reserve} />
      </header>

      <div className="reserve-gauge">
        <div
          className="reserve-meter"
          role="meter"
          aria-label={pool ? "Free on the shelf" : "Held"}
          aria-valuemin={0}
          aria-valuemax={reserve.target}
          aria-valuenow={reserve.count}
        >
          <span
            className="reserve-meter-fill"
            style={{ width: `${Math.max(fullness * 100, reserve.count ? 3 : 0)}%` }}
          />
          {reserve.minimum > 0 && reserve.minimum < reserve.target && (
            <span
              className="reserve-meter-floor"
              style={{ left: `${floor * 100}%` }}
              title="Alerts below this"
            />
          )}
        </div>
        <p className="reserve-numbers">
          <strong>{formatCount(reserve.count)}</strong> {pool ? "free on the shelf" : "held"}
          <span className="muted">
            {" "}
            · alerts below {reserve.minimum}
            {reserve.target !== reserve.minimum && ` · refills to ${reserve.target}`}
            {!pool && ` · ${formatCount(reserve.free)} more matching on the shelf`}
          </span>
          {/* On the same line, so a short card is no taller than a stocked one. */}
          {reserve.status !== "ok" && reserveShortfall(reserve) > 0 && (
            <span className="reserve-order"> · order {formatCount(reserveShortfall(reserve))}</span>
          )}
        </p>
      </div>

      {(reserve.credit > 0 || reserve.note) && (
        <p className="reserve-meta">
          {reserve.credit > 0 && (
            <span className="pill pill-warn">{plural(reserve.credit, "unit")} paid for, not yet held</span>
          )}
          {reserve.note && <span className="muted">{reserve.note}</span>}
        </p>
      )}

      {error && <Notice tone="error">{error}</Notice>}

      {quieting ? (
        <QuietForm
          reserve={reserve}
          onCancel={() => setQuieting(false)}
          onSaved={(next) => {
            setQuieting(false);
            onChanged(next);
          }}
        />
      ) : (
        // One row whatever the state: the one thing to do now, the rest in a
        // menu. Short cards used to wrap four buttons onto two rows.
        <div className="reserve-actions">
          {canFill ? (
            <button type="button" className="button button-small" disabled={busy} onClick={() => void fill()}>
              <Icon name="download" size={16} />
              Fill from shelf
            </button>
          ) : reserve.status === "short" ? (
            <button type="button" className="button button-small" onClick={() => setQuieting(true)}>
              <Icon name="clock" size={16} />
              Mark as ordered…
            </button>
          ) : null}
          <Menu
            label={`More for ${reserveTagLine(reserve.tags)}`}
            buttonClassName="icon-button icon-button-sm"
            button={<Icon name="more" size={18} />}
          >
            {(close) => (
              <>
                {canFill && reserve.status === "short" && (
                  <MenuItem
                    icon={<Icon name="clock" />}
                    onClick={() => {
                      close();
                      setQuieting(true);
                    }}
                  >
                    Mark as ordered…
                  </MenuItem>
                )}
                {reserve.status === "quiet" && (
                  <MenuItem
                    icon={<Icon name="x" />}
                    onClick={() => {
                      close();
                      void unquiet();
                    }}
                  >
                    Clear {reserve.quietReason === "snoozed" ? "snooze" : "order"}
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Icon name="pencil" />}
                  onClick={() => {
                    close();
                    onEdit();
                  }}
                >
                  Edit
                </MenuItem>
              </>
            )}
          </Menu>
          <button
            type="button"
            className="button button-ghost button-small reserve-toggle"
            aria-haspopup="dialog"
            onClick={() => {
              setDetailError(null);
              onOpen();
            }}
          >
            <Icon name="bookmark" size={16} />
            {pool ? "Matching stock" : `Held units (${reserve.count})`}
          </button>
        </div>
      )}

      {open && (
        <Dialog wide title={`${reserveTagLine(reserve.tags)} · ${reserveOwner(reserve)}`} onClose={onClose}>
          {detailError && <Notice tone="error">{detailError}</Notice>}
          {detail === null ? (
            !detailError && <div className="skeleton-line" />
          ) : (
            <ReserveContents
              detail={detail}
              markup={markup}
              onChanged={settle}
              onOpenItem={onOpenItem}
              onError={setDetailError}
            />
          )}
        </Dialog>
      )}
    </article>
  );
}

function StatusPill({ reserve }: { reserve: Reserve }) {
  if (reserve.status === "ok") {
    return (
      <span className="pill pill-ok">
        <Icon name="check" size={13} />
        Stocked
      </span>
    );
  }
  if (reserve.status === "quiet" && reserve.quietUntil) {
    return (
      <span className="pill pill-warn" title={reserve.quietNote || undefined}>
        <Icon name="clock" size={13} />
        {reserve.quietReason === "snoozed" ? "Snoozed to" : "Ordered, due"} {calendarDay(reserve.quietUntil)}
      </span>
    );
  }
  return (
    <span className="pill pill-danger">
      <Icon name="alert" size={13} />
      Short {Math.max(reserve.minimum - reserve.count, 0)}
    </span>
  );
}

/* ---------------------------------------------- what it holds and could -- */

function ReserveContents({
  detail,
  markup,
  onChanged,
  onOpenItem,
  onError,
}: {
  detail: ReserveDetail;
  markup: number;
  onChanged: (detail: ReserveDetail) => void;
  onOpenItem: (id: string) => void;
  onError: (message: string | null) => void;
}) {
  const { reserve, holds, candidates } = detail;
  const pool = reserve.clientId === null;
  const [picked, setPicked] = useState<Map<string, { quantity: string; price: string }>>(new Map());
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const toggle = (itemId: string, cost: number | null) =>
    setPicked((now) => {
      const next = new Map(now);
      if (next.has(itemId)) next.delete(itemId);
      else next.set(itemId, { quantity: "1", price: priceFromCents(priceWithMarkup(cost, markup)) });
      return next;
    });

  const change = (itemId: string, patch: Partial<{ quantity: string; price: string }>) =>
    setPicked((now) => new Map(now).set(itemId, { ...now.get(itemId)!, ...patch }));

  async function holdPicked() {
    const lines = [];
    for (const candidate of candidates) {
      const line = picked.get(candidate.itemId);
      if (!line) continue;
      const quantity = candidate.tracking === "bulk" ? Number(line.quantity) : 1;
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > candidate.free) {
        return onError(`${candidate.title || "Untitled item"}: hold between 1 and ${candidate.free}.`);
      }
      const price = reserve.prepaid ? centsFromInput(line.price) : null;
      if (reserve.prepaid && price === null) {
        return onError(
          `${candidate.title || "Untitled item"} needs a price: this client pays when stock is held.`,
        );
      }
      lines.push({
        itemId: candidate.itemId,
        quantity,
        ...(price !== null ? { unitPriceCents: price } : {}),
      });
    }
    setBusy(true);
    onError(null);
    try {
      const next = await api.reserves.hold(reserve.id, { heldOn: todayLocal(), lines });
      toast(`Held for ${reserveOwner(reserve)}.`);
      setPicked(new Map());
      onChanged(next);
    } catch (problem) {
      onError(problem instanceof ApiError ? problem.message : "That did not hold.");
    } finally {
      setBusy(false);
    }
  }

  async function release(hold: ReserveHold, quantity: number) {
    setBusy(true);
    onError(null);
    try {
      const next = await api.reserves.release(hold.id, { quantity, releasedOn: todayLocal() });
      toast("Back on the shelf, free for anybody.");
      onChanged(next);
    } catch (problem) {
      onError(problem instanceof ApiError ? problem.message : "That did not release.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="reserve-contents">
      {!pool && (
        <>
          <h3 className="facts-title">Held for {reserve.clientName}</h3>
          {holds.length === 0 ? (
            <p className="muted small">
              Nothing held yet. Pick from the matching stock below, or fill from the shelf.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Serial</th>
                  <th>Units</th>
                  <th>Held since</th>
                  <th>Paid</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {holds.map((hold) => (
                  <HoldRow key={hold.id} hold={hold} busy={busy} onOpen={onOpenItem} onRelease={release} />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <h3 className="facts-title">
        {pool ? "Counted: matching stock nobody holds" : "Matching on the shelf, free to hold"}
      </h3>
      {candidates.length === 0 ? (
        <p className="muted small">
          No free stock on the shelf wears {reserve.tags.length === 1 ? "that tag" : "all of those tags"}.
        </p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                {!pool && (
                  <th>
                    <span className="sr-only">Pick</span>
                  </th>
                )}
                <th>Item</th>
                <th>Serial</th>
                <th>Free</th>
                {!pool && <th>Units to hold</th>}
                {!pool && reserve.prepaid && <th>Price each</th>}
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate) => {
                const line = picked.get(candidate.itemId);
                const name = candidate.title || "Untitled item";
                return (
                  <tr key={candidate.itemId}>
                    {!pool && (
                      <td data-label="Pick">
                        <input
                          type="checkbox"
                          className="check"
                          aria-label={`Hold ${name}`}
                          checked={Boolean(line)}
                          onChange={() => toggle(candidate.itemId, candidate.unitCostCents)}
                        />
                      </td>
                    )}
                    <td data-label="Item">
                      <button type="button" className="link" onClick={() => onOpenItem(candidate.itemId)}>
                        {name}
                      </button>
                    </td>
                    <td data-label="Serial" className="mono">
                      {candidate.serial || <span className="faint">none</span>}
                    </td>
                    <td data-label="Free">{formatCount(candidate.free)}</td>
                    {!pool && (
                      <td data-label="Units to hold">
                        {candidate.tracking === "bulk" && line ? (
                          <input
                            className="input input-small input-number"
                            type="number"
                            min={1}
                            max={candidate.free}
                            aria-label={`Units of ${name} to hold`}
                            value={line.quantity}
                            onChange={(event) => change(candidate.itemId, { quantity: event.target.value })}
                          />
                        ) : (
                          <span className="muted">{line ? "1" : ""}</span>
                        )}
                      </td>
                    )}
                    {!pool && reserve.prepaid && (
                      <td data-label="Price each">
                        {line ? (
                          <input
                            className="input input-small input-number"
                            inputMode="decimal"
                            aria-label={`Price for ${name}`}
                            value={line.price}
                            onChange={(event) => change(candidate.itemId, { price: event.target.value })}
                          />
                        ) : (
                          <span className="muted">
                            {formatMoney(priceWithMarkup(candidate.unitCostCents, markup)) || "n/a"}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!pool && (
            <div className="panel-foot">
              <span className="muted small">
                {reserve.prepaid
                  ? `Paid in advance: held units go on the next billing export${reserve.credit > 0 ? `, after the ${plural(reserve.credit, "unit")} already paid for` : ""}.`
                  : "Held stock stays on the shelf, marked for this client."}
              </span>
              <button
                type="button"
                className="button button-small"
                disabled={busy || picked.size === 0}
                onClick={() => void holdPicked()}
              >
                <Icon name="bookmark" size={16} />
                Hold {picked.size > 0 ? plural(picked.size, "item") : "picked"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HoldRow({
  hold,
  busy,
  onOpen,
  onRelease,
}: {
  hold: ReserveHold;
  busy: boolean;
  onOpen: (id: string) => void;
  onRelease: (hold: ReserveHold, quantity: number) => void;
}) {
  const [count, setCount] = useState(String(hold.open));
  const paid =
    hold.billing === "credit" ? (
      <span className="pill">Already paid for</span>
    ) : hold.billing === "bill" ? (
      hold.billedAt ? (
        <span className="pill pill-ok">Billed {formatMoney(hold.unitPriceCents)}</span>
      ) : (
        <span className="pill pill-warn">Waiting {formatMoney(hold.unitPriceCents)}</span>
      )
    ) : (
      <span className="faint">n/a</span>
    );
  return (
    <tr>
      <td data-label="Item">
        <button type="button" className="link" onClick={() => onOpen(hold.itemId)}>
          {hold.title || "Untitled item"}
        </button>
      </td>
      <td data-label="Serial" className="mono">
        {hold.serial || <span className="faint">none</span>}
      </td>
      <td data-label="Units">
        {hold.tracking === "bulk" && hold.open > 1 ? (
          <input
            className="input input-small input-number"
            type="number"
            min={1}
            max={hold.open}
            aria-label={`Units of ${hold.title} to release`}
            value={count}
            onChange={(event) => setCount(event.target.value)}
          />
        ) : (
          hold.open
        )}
      </td>
      <td data-label="Held since">{calendarDay(hold.heldOn)}</td>
      <td data-label="Paid">{paid}</td>
      <td className="cell-action">
        <button
          type="button"
          className="button button-ghost button-small"
          disabled={busy}
          onClick={() => {
            const quantity = hold.tracking === "bulk" ? Number(count) : 1;
            if (Number.isInteger(quantity) && quantity >= 1 && quantity <= hold.open)
              onRelease(hold, quantity);
          }}
        >
          Release
        </button>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------- ordered -- */

function QuietForm({
  reserve,
  onCancel,
  onSaved,
}: {
  reserve: Reserve;
  onCancel: () => void;
  onSaved: (reserve: Reserve) => void;
}) {
  const inAWeek = new Date();
  inAWeek.setDate(inAWeek.getDate() + 7);
  const [reason, setReason] = useState<ReserveQuietReason>("ordered");
  const [until, setUntil] = useState(todayLocal(inAWeek));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { reserve: next } = await api.reserves.quiet(reserve.id, { until, reason, note });
      onSaved(next);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not save.");
      setBusy(false);
    }
  }

  return (
    <form className="reserve-quiet-form" onSubmit={submit}>
      <Segmented<ReserveQuietReason>
        label="Why it is quiet"
        size="sm"
        value={reason}
        onChange={setReason}
        options={[
          { value: "ordered", label: "Ordered" },
          { value: "snoozed", label: "Snooze" },
        ]}
      />
      <div className="split">
        <label className="field">
          <span className="field-label">{reason === "ordered" ? "Expected by" : "Quiet until"}</span>
          <input
            className="input"
            type="date"
            required
            min={todayLocal()}
            value={until}
            onChange={(event) => setUntil(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">
            {reason === "ordered" ? "PO or note (optional)" : "Note (optional)"}
          </span>
          <input
            className="input"
            maxLength={200}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
      </div>
      <p className="muted small">
        Alerts leave it out until that day. If it is still short after that, it is back in the next digest.
      </p>
      {error && <Notice tone="error">{error}</Notice>}
      <div className="panel-foot">
        <button type="button" className="button button-quiet button-small" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="button button-small" disabled={busy}>
          Save
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------ the rule -- */

function ReserveForm({
  reserve,
  clients,
  reserveTags,
  onReserveTags,
  onClients,
  onCancel,
  onSaved,
  onDeleted,
}: {
  reserve: Reserve | null;
  clients: Client[];
  reserveTags: ReserveTag[];
  onReserveTags: (tags: ReserveTag[]) => void;
  onClients: (clients: Client[]) => void;
  onCancel: () => void;
  onSaved: (reserve: Reserve) => void;
  onDeleted: () => void;
}) {
  const [clientId, setClientId] = useState(reserve ? (reserve.clientId ?? "") : (clients[0]?.id ?? ""));
  const [newClient, setNewClient] = useState("");
  const [tags, setTags] = useState<string[]>(reserve?.tags ?? []);
  const [minimum, setMinimum] = useState(String(reserve?.minimum ?? 1));
  const [target, setTarget] = useState(String(reserve?.target ?? reserve?.minimum ?? 1));
  const [prepaid, setPrepaid] = useState(reserve?.prepaid ?? false);
  const [credit, setCredit] = useState(String(reserve?.credit ?? 0));
  const [note, setNote] = useState(reserve?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const pool = clientId === "";

  async function addClient() {
    const name = newClient.trim();
    if (!name) return;
    try {
      const { clients: next } = await api.clients.create(name);
      onClients(next);
      setClientId(next.find((one) => one.name.toLowerCase() === name.toLowerCase())?.id ?? "");
      setNewClient("");
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add that client.");
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    const min = Number(minimum);
    const fill = target.trim() === "" ? min : Number(target);
    if (tags.length === 0)
      return setError("Pick at least one reserve tag for this reserve to match stock by.");
    if (!Number.isInteger(min) || min < 0) return setError("The alert number is a whole number, 0 or more.");
    if (!Number.isInteger(fill) || fill < min) return setError("Refill up to at least the alert number.");
    const input: ReserveInput = {
      clientId: pool ? null : clientId,
      tags,
      minimum: min,
      target: fill,
      prepaid: !pool && prepaid,
      note,
      ...(reserve && !pool && prepaid ? { credit: Number(credit) || 0 } : {}),
    };
    setBusy(true);
    try {
      const { reserve: saved } = reserve
        ? await api.reserves.update(reserve.id, input)
        : await api.reserves.create(input);
      toast(reserve ? "Reserve saved." : "Reserve made.");
      onSaved(saved);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not save.");
      setBusy(false);
    }
  }

  async function remove() {
    if (!reserve) return;
    const holding =
      reserve.clientId && reserve.count > 0
        ? ` The ${plural(reserve.count, "unit")} it holds go back on the shelf.`
        : "";
    if (!window.confirm(`Delete this reserve?${holding}`)) return;
    setBusy(true);
    try {
      await api.reserves.remove(reserve.id, todayLocal());
      toast("Reserve deleted.");
      onDeleted();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not delete.");
      setBusy(false);
    }
  }

  return (
    <Panel
      // Opened by Edit on a card further down: bring it into view, or it looks like nothing happened.
      reveal
      title={reserve ? `Edit ${reserveTagLine(reserve.tags)} for ${reserveOwner(reserve)}` : "New reserve"}
      note="Pick the reserve tags stock has to wear, every one of them. A new one made here is offered on the receiving screen from then on, so stock is tagged to match as it comes in."
    >
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span className="field-label">For</span>
          <select className="input" value={clientId} onChange={(event) => setClientId(event.target.value)}>
            <option value="">General pool (no client)</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
        {!reserve && (
          <div className="inline-add">
            <Icon name="plus" size={18} />
            <input
              className="input"
              aria-label="A new client's name"
              placeholder="Or add a new client"
              value={newClient}
              onChange={(event) => setNewClient(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void addClient();
                }
              }}
            />
            <button
              type="button"
              className="button button-quiet"
              disabled={!newClient.trim()}
              onClick={() => void addClient()}
            >
              Add
            </button>
          </div>
        )}
        {pool && (
          <p className="muted small">
            A general-pool rule holds nothing. It counts matching stock on the shelf that nobody holds, and
            alerts when that runs low.
          </p>
        )}

        <ReserveTagPicker
          label="Matches stock tagged"
          lead="Counts stock tagged"
          tags={reserveTags}
          selected={tags}
          onChange={setTags}
          onCreate={async (name) => {
            try {
              const { reserveTags: next } = await api.reserves.createTag(name);
              onReserveTags(next);
              return next.find((tag) => tag.name.toLowerCase() === name.toLowerCase())?.name ?? name;
            } catch (problem) {
              setError(problem instanceof ApiError ? problem.message : "Could not make that reserve tag.");
              return null;
            }
          }}
        />

        <div className="split">
          <label className="field">
            <span className="field-label">Alert below</span>
            <input
              className="input input-number"
              type="number"
              min={0}
              required
              value={minimum}
              onChange={(event) => setMinimum(event.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Refill up to</span>
            <input
              className="input input-number"
              type="number"
              min={0}
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            />
          </label>
        </div>
        <ReserveRangePreview minimum={minimum} target={target} pool={pool} />

        {!pool && (
          <label className="toggle">
            <input type="checkbox" checked={prepaid} onChange={(event) => setPrepaid(event.target.checked)} />
            <span className="toggle-track" />
            <span>
              Paid in advance
              <span className="toggle-hint">
                The client pays for stock when it is held: each held unit is a line on the next billing
                export, and deploying it to them later bills nothing more. Units taken for another client are
                kept as credit.
              </span>
            </span>
          </label>
        )}
        {reserve && !pool && prepaid && (
          <label className="field">
            <span className="field-label">Paid for, not yet held</span>
            <input
              className="input input-number"
              type="number"
              min={0}
              value={credit}
              onChange={(event) => setCredit(event.target.value)}
            />
          </label>
        )}

        <label className="field">
          <span className="field-label">Note (optional)</span>
          <input className="input" value={note} onChange={(event) => setNote(event.target.value)} />
        </label>

        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          {reserve && (
            <button
              type="button"
              className="button button-danger"
              disabled={busy}
              onClick={() => void remove()}
            >
              <Icon name="trash" size={16} />
              Delete
            </button>
          )}
          <span className="panel-foot-spacer" />
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy}>
            {busy ? "Saving…" : reserve ? "Save" : "Make reserve"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/* ---------------------------------------------------------- reserve tags -- */

/**
 * The stockroom's reserve tags: what reserves match on, and what the
 * receiving screen offers to put on a line. Removing one leaves every item
 * wearing it as a plain tag; one a reserve still matches on stays.
 *
 * Renaming one renames it everywhere at once -- on every item that wears it
 * and in every rule that matches on it -- so a rule never stops matching
 * because its tag was respelled.
 */
function ReserveTagsPanel({
  tags,
  onChange,
  onRenamed,
}: {
  tags: ReserveTag[];
  onChange: (tags: ReserveTag[]) => void;
  onRenamed: () => void;
}) {
  const [text, setText] = useState("");
  const [renaming, setRenaming] = useState<{ from: string; to: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  async function run(work: () => Promise<{ reserveTags: ReserveTag[] }>, done: string) {
    setBusy(true);
    setError(null);
    try {
      onChange((await work()).reserveTags);
      toast(done);
      return true;
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not save.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title="Reserve tags"
      note="The tags reserves match on. Pick them onto each line when you receive stock, and items wear them with a tag icon so it is plain which tags count toward back stock."
    >
      {tags.length === 0 ? (
        <p className="muted small">None yet. Making a reserve makes its tags, or add one here.</p>
      ) : (
        <ul className="reserve-tag-list">
          {tags.map((tag) =>
            renaming?.from === tag.name ? (
              <li key={tag.name}>
                <form
                  className="inline-add reserve-tag-rename"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const to = renaming.to.trim();
                    if (!to || to === tag.name) return setRenaming(null);
                    void run(() => api.reserves.renameTag(tag.name, to), `Renamed to “${to}”.`).then((ok) => {
                      if (!ok) return;
                      setRenaming(null);
                      onRenamed();
                    });
                  }}
                >
                  <Icon name="tag" size={16} />
                  <input
                    className="input input-small"
                    aria-label={`New name for reserve tag ${tag.name}`}
                    autoFocus
                    value={renaming.to}
                    onChange={(event) => setRenaming({ from: tag.name, to: event.target.value })}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setRenaming(null);
                    }}
                  />
                  <button
                    type="button"
                    className="button button-quiet button-small"
                    onClick={() => setRenaming(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="button button-small"
                    disabled={busy || !renaming.to.trim()}
                  >
                    Rename
                  </button>
                </form>
                <span className="muted small">
                  Renames it on {plural(tag.itemCount, "item")} and in {plural(tag.reserveCount, "reserve")}.
                </span>
              </li>
            ) : (
              <li key={tag.name}>
                <ReserveTagChip
                  name={tag.name}
                  {...(tag.reserveCount === 0
                    ? {
                        onRemove: () =>
                          void run(
                            () => api.reserves.removeTag(tag.name),
                            `“${tag.name}” is a plain tag again.`,
                          ),
                      }
                    : {})}
                />
                <span className="muted small">
                  {plural(tag.itemCount, "item")} · {plural(tag.reserveCount, "reserve")}
                </span>
                <button
                  type="button"
                  className="icon-button icon-button-sm"
                  aria-label={`Rename reserve tag ${tag.name}`}
                  title="Rename"
                  disabled={busy}
                  onClick={() => {
                    setError(null);
                    setRenaming({ from: tag.name, to: tag.name });
                  }}
                >
                  <Icon name="pencil" size={16} />
                </button>
              </li>
            ),
          )}
        </ul>
      )}
      <form
        className="inline-add"
        onSubmit={(event) => {
          event.preventDefault();
          const name = text.trim();
          if (!name) return;
          void run(() => api.reserves.createTag(name), `“${name}” is a reserve tag.`).then(
            (ok) => ok && setText(""),
          );
        }}
      >
        <Icon name="tag" size={18} />
        <input
          className="input"
          aria-label="A new reserve tag"
          placeholder="New reserve tag, e.g. 256 GB"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <button type="submit" className="button button-quiet" disabled={busy || !text.trim()}>
          Add
        </button>
      </form>
      {error && <Notice tone="error">{error}</Notice>}
    </Panel>
  );
}

/* ---------------------------------------------------------------- alerts -- */

function AlertsPanel({ prefs, onSaved }: { prefs: AlertPrefs; onSaved: (prefs: AlertPrefs) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  async function choose(frequency: AlertFrequency) {
    setBusy(true);
    setError(null);
    try {
      const { alerts } = await api.reserves.saveAlerts({ frequency, timeZone: zone });
      onSaved(alerts);
      toast(
        frequency === "off"
          ? "Reserve alerts are off."
          : `You will get ${ALERT_FREQUENCY_LABELS[frequency].toLowerCase()} reserve alerts.`,
      );
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not save.");
    } finally {
      setBusy(false);
    }
  }

  const hour = new Date(2000, 0, 1, DIGEST_HOUR).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const explain: Record<AlertFrequency, string> = {
    off: "No email. Short reserves still show here and on the Stock screen.",
    hourly: "At most one email an hour, and only when something new has gone short.",
    daily: `A summary at ${hour} each morning (${prefs.timeZone.replace(/_/g, " ")}), when anything is short.`,
    weekly: `A summary at ${hour} on Mondays (${prefs.timeZone.replace(/_/g, " ")}), when anything is short.`,
  };

  return (
    <section id="alerts" className="reserve-alerts">
      <Panel
        title="Email me about short reserves"
        note={`Just for you, for this stockroom. A reserve has to stay short for ${ALERT_GRACE_SECONDS / 60} minutes before any email mentions it, so moving stock between clients does not set one off.`}
      >
        <Segmented<AlertFrequency>
          label="How often"
          value={prefs.frequency}
          disabled={busy}
          onChange={(next) => void choose(next)}
          options={(["off", "hourly", "daily", "weekly"] as const).map((value) => ({
            value,
            label: ALERT_FREQUENCY_LABELS[value],
          }))}
        />
        <p className="muted small reserve-alerts-help">{explain[prefs.frequency]}</p>
        {error && <Notice tone="error">{error}</Notice>}
      </Panel>
    </section>
  );
}

/**
 * The two numbers on the reserve form, drawn: the stretch below the first is
 * where it alerts, the stretch up to the second is where a refill aims. Says
 * the same thing in a sentence under it, so the bar is never the only telling.
 */
function ReserveRangePreview({ minimum, target, pool }: { minimum: string; target: string; pool: boolean }) {
  const min = Number(minimum);
  const fill = target.trim() === "" ? min : Number(target);
  if (!Number.isInteger(min) || min < 0 || !Number.isInteger(fill) || fill < min) return null;
  const top = Math.max(fill, 1);
  const what = pool ? "free on the shelf" : "held";
  return (
    <div className="reserve-range" aria-hidden="true">
      <div className="reserve-range-bar">
        {min > 0 && (
          <span className="reserve-range-low" style={{ width: `${(min / top) * 100}%` }}>
            Alerts
          </span>
        )}
        {fill > min && (
          <span className="reserve-range-ok" style={{ width: `${((fill - min) / top) * 100}%` }}>
            OK
          </span>
        )}
      </div>
      <div className="reserve-range-scale">
        <span>0</span>
        {min > 0 && min < fill && (
          <span className="reserve-range-tick" style={{ left: `${(min / top) * 100}%` }}>
            {min}
          </span>
        )}
        <span>{fill}</span>
      </div>
      <p className="muted small">
        {min === 0
          ? "Never alerts. "
          : `Alerts when fewer than ${plural(min, "unit")} ${min === 1 ? "is" : "are"} ${what}. `}
        {pool
          ? `Reorder suggestions bring it back up to ${fill}.`
          : `“Fill from shelf” and reorder suggestions bring it back up to ${fill}.`}
      </p>
    </div>
  );
}
